import React, { useEffect, useMemo, useRef } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetFootprintingPorkManureStorage,
  useGetPorkManureStorage,
  useKeyedFormList,
  useMutateFootprintingPorkManureStorage,
  useMutateFootprintingPorkPrimaryData,
  useTranslation,
} from "utils";

import { PORK_PRACTICE_SLUG, UploadableManureStorage } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  Select,
  Typography,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const manureStoragePlaceholder: UploadableManureStorage = {
  id: undefined,
  manureStorageId: "",
  amountStoredPct: null,
};

const PorkManureStorageForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation(["footprintingPork", "pork"]);
  const formRef = useRef<FormInstance>(null);

  const manureStorageUsagesFullQuery = useGetFootprintingPorkManureStorage(primaryDataId);
  const { data: manureStorageUsages } = manureStorageUsagesFullQuery;
  const manureStoragesFullQuery = useGetPorkManureStorage();
  const { data: manureStorages } = manureStoragesFullQuery;
  const isLoading = [manureStorageUsagesFullQuery, manureStoragesFullQuery].some(queryIsLoading);
  const keyedManureStorage = useKeyedFormList<UploadableManureStorage>(formRef, { isReady: !isLoading });

  const { mutate: mutatePorkPrimaryData } = useMutateFootprintingPorkPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      isSubmitted: false,
    },
    primaryDataId
  );

  const selectGroupName = "footprinting-pork-manure-storage-select";

  const { mutate: mutatePorkManureStorage } = useMutateFootprintingPorkManureStorage(
    farmId,
    year,
    {
      primaryDataId: primaryDataId ?? "",
      selectedManureStorage: keyedManureStorage.payload.filter((m) => !!m.manureStorageId),
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePorkManureStorage();
      }
    },
    [isLoading, mutatePorkManureStorage, mutatePorkPrimaryData, readOnly]
  );

  // Load manure management data
  useEffect(() => {
    if (manureStorageUsages) {
      keyedManureStorage.actions.hydrate(
        manureStorageUsages.length
          ? manureStorageUsages.map((m) => ({
              id: m.id,
              manureStorageId: m.manureStorageId ?? "",
              amountStoredPct: m.amountStoredPct ?? null,
            }))
          : [manureStoragePlaceholder]
      );
      // Saved entries are checked straight away; a first-visit placeholder stays neutral
      if (manureStorageUsages.length) keyedManureStorage.actions.requestRehydrationValidation();
    }
  }, [manureStorageUsages, keyedManureStorage.actions]);

  const totalStoragePercentage = useMemo(
    () => keyedManureStorage.entries.reduce((total, storage) => total + (formatFractionInput(storage.amountStoredPct) || 0), 0),
    [keyedManureStorage.entries]
  );

  const hasLoadedManureStorage = keyedManureStorage.entries.length > 0 && manureStorageUsages !== undefined;

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef} preserve={false}>
      <Card border dataTestId="footprinting-pork-manure-storage-form" className="padding-200">
        <Flex vertical gap={16}>
          <Flex vertical>
            <Typography variant="h3">{t("porkManureStorageForm.header")}</Typography>
            <Typography variant="paragraph-m" color="grey-600" className="margin-top-25">
              {t("porkManureStorageForm.description")}
            </Typography>
          </Flex>

          {hasLoadedManureStorage && (
            <ProgressBarCardWithContent
              currentValue={totalStoragePercentage}
              title={t("porkManureStorageForm.progressBar.title")}
              // description={t("porkManureStorageForm.progressBar.description")} // design decision to comment this out for now.
              errorMessage={t("porkManureStorageForm.progressBar.errorMessage")}
              successMessage={t("porkManureStorageForm.progressBar.successMessage")}
              size={8}
            />
          )}

          {keyedManureStorage.entries.map((manure, index) => {
            const selectGroupWithIndex = `${selectGroupName}-${index}`;

            return (
              <Flex key={manure.clientKey} vertical gap={16}>
                <Flex justify="space-between" align="center" vertical={false}>
                  <Typography variant="h5" style={{ flex: 1 }}>
                    {t("porkManureStorageForm.storageNumber", { count: index + 1 })}
                  </Typography>
                  <IconButton
                    type="tertiary"
                    disabled={readOnly}
                    dataTestId={`delete-manure-storage-${index}`}
                    size="small"
                    iconName="tabler/trash"
                    onClick={() => keyedManureStorage.actions.remove(manure.clientKey)}
                  />
                </Flex>
                <Select
                  showSearch={true}
                  disabled={readOnly}
                  name={`${selectGroupName}-${manure.clientKey}`}
                  dataTestId={selectGroupWithIndex}
                  label={t("porkManureStorageForm.question.manureStorageTypeQuestionInput")}
                  rules={[{ required: true, message: "" }]}
                  initialValue={manureStorages?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                  value={manureStorages?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                  onChange={(v) => {
                    const matchingPractice = manureStorages?.find((m) => m.name === v);
                    keyedManureStorage.actions.update(manure.clientKey, { manureStorageId: matchingPractice ? matchingPractice.id : "" });
                  }}
                  dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                >
                  {(manureStorages ?? []).map((porkMS) => {
                    // Check if this storage ID is already used in another item
                    const isAlreadyUsed = keyedManureStorage.entries.some(
                      (m) => m.manureStorageId === porkMS.id && m.manureStorageId !== manure.manureStorageId
                    );

                    return (
                      <Option
                        disabled={readOnly || isAlreadyUsed}
                        key={`${selectGroupWithIndex}-${porkMS.id}`}
                        data-testid={`${selectGroupWithIndex}-${porkMS.id}`}
                        value={porkMS.name}
                      >
                        {t(`${PORK_PRACTICE_SLUG.MANURE_STORAGE}.${porkMS.translationSlug}`, { ns: "pork" }) || porkMS.name}
                      </Option>
                    );
                  })}
                </Select>

                <InputNumber
                  key={`amountStoredPct-${manure.clientKey}-${manure.manureStorageId}`}
                  disabled={readOnly}
                  name={`amountStoredPct-${manure.clientKey}`}
                  dataTestId="footprinting-manure-storage-pct-input"
                  label={t("porkManureStorageForm.question.manureStoragePctQuestionInput")}
                  suffix="%"
                  value={formatFractionInput(manure.amountStoredPct)}
                  initialValue={formatFractionInput(manure.amountStoredPct)}
                  onChange={(v) => keyedManureStorage.actions.update(manure.clientKey, { amountStoredPct: formatPercentageInput(v) })}
                  validateFirst
                  rules={[
                    { required: true, message: t("porkManureStorageForm.error.validPercentage") },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                          return Promise.reject(new Error(t("porkManureStorageForm.error.validPercentage")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Flex>
            );
          })}

          <Button
            type="text"
            dataTestId="add-manure-storage-button"
            disabled={readOnly}
            onClick={() => {
              keyedManureStorage.actions.add(manureStoragePlaceholder);
            }}
          >
            <Icon iconName="klim/plus" className="icon inline" /> {t("porkManureStorageForm.add")}
          </Button>
        </Flex>
      </Card>
    </Form>
  );
};

PorkManureStorageForm.Key = "pork.manureStorage";
export { PorkManureStorageForm };
